/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react'
import intl from 'react-intl-universal'
import RegenerateAllModal from './RegenerateAllModal'
import RegenerateInput from './RegenerateInput'
import $ from 'jquery'
import './styles.module.scss'
import Snackbar from '~/reactapp/shared/Snackbar'
import { concurrentBulkSaveCirculations } from '../../../api/story'
import { composeWebsitesFromCirculations } from '../CirculateTab/CirculateTab.utils'
import { Circulation } from '~/types'

const TOO_MANY_REQUEST = 429

interface CanonicalUrlProps {
  websitesObj: any
  storyObj: any
  circulations?: any
  handleSingleRegeneration?: (id: string) => Promise<void>
  hasRegeneratePermissions: boolean
  isScheduled?: boolean
}

interface ISnackbar {
  success?: boolean
  error?: boolean
  timeout?: boolean
  tooManyRequest?: boolean
}

export interface IWebsite {
  id: string
  website: {
    website_section: {
      type: string
      referent: {
        id: string
        provider: string
        type: string
        website: string
      }
    }
    website_url: string
    success?: boolean
    errorCode?: number
  }
}

export default function CanonicalUrl({
  websitesObj,
  storyObj,
  handleSingleRegeneration,
  hasRegeneratePermissions,
  circulations,
  isScheduled,
}: CanonicalUrlProps) {
  const [isDirty, setIsDirty] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false)
  const [websites, setWebsites] = useState<Array<IWebsite>>([])
  const [snackbar, setSnackbar] = useState<ISnackbar>({
    success: false,
    error: false,
    timeout: false,
    tooManyRequest: false,
  })
  const isPublished = storyObj.additional_properties.is_published

  function hasOnErrorResponse(response) {
    if (!response.error) {
      return false
    }

    const errors = {
      '404': 'canonicalUrl.regenAll.snackbar.notFound',
      '429': 'canonicalUrl.regenAll.snackbar.tooManyRequest',
      '504': 'canonicalUrl.regenAll.snackbar.timeout',
    }

    setErrorMessage(
      intl.formatMessage({
        id:
          errors[response.error.code] ?? 'canonicalUrl.regenAll.error.network',
      })
    )

    if (response?.error?.value) {
      setSnackbar({
        ...snackbar,
        timeout: true,
      })
      setIsRegenerating(false)
      setIsDirty(true)
      return true
    }
  }

  const websitesCount = useMemo(() => {
    return {
      success: websites.filter(({ website }) => website.success === true)
        .length,
      error: websites.filter(({ website }) => !website.success).length,
      tooManyRequest: websites.filter(
        ({ website }) => website.errorCode === TOO_MANY_REQUEST
      ).length,
    }
  }, [websites])

  async function handleRegenerateAll() {
    setIsRegenerating(true)

    const response = await concurrentBulkSaveCirculations(
      circulations,
      storyObj._id
    )

    if (hasOnErrorResponse(response)) {
      return null
    }

    const updatedWebsites = composeWebsitesFromCirculations(
      response.circulations as Circulation[]
    )
    const websiteArray: Array<IWebsite> = []
    for (const id in updatedWebsites) {
      websiteArray.push({
        id,
        website: updatedWebsites[id],
      })
    }
    setWebsites(websiteArray)
    setIsRegenerating(false)
    setIsDirty(true)
  }

  const showRegenerateModal = () => {
    $('#regenerateAllCanonicalUrlModal').modal('show').css('display', 'flex')
  }

  useEffect(() => {
    const websiteArray: Array<IWebsite> = []
    for (const id in websitesObj) {
      websiteArray.push({
        id,
        website: websitesObj[id],
      })
    }

    setWebsites(websiteArray)
  }, [])

  useEffect(() => {
    setSnackbar({
      ...snackbar,
      success: websitesCount.success > 0,
      error: websitesCount.error > 0,
      tooManyRequest: websitesCount.tooManyRequest > 0,
    })
  }, [websitesCount])

  return (
    <>
      <section data-testid="canonical-url-react" className="canonical-url">
        <div
          className={
            isRegenerating
              ? 'canonical-url--header-is-regenerating-all'
              : 'canonical-url--header'
          }
        >
          <div className="canonical-url--header-title">
            <h4 className="title">
              {intl.get('canonicalUrl.websiteUrl.label')}
            </h4>
          </div>
          <div className="canonical-url--header-button">
            <button
              className={
                !isRegenerating ? 'btn btn-primary' : 'btn btn-is-regenerating'
              }
              onClick={showRegenerateModal}
              data-testid="canonical-url-regen-all-button"
              disabled={
                (isRegenerating || !hasRegeneratePermissions || !isPublished) &&
                !isScheduled
              }
            >
              {isRegenerating && (
                <>
                  <i className="fa fa-refresh" aria-hidden="true"></i>
                  {intl.get('canonicalUrl.regenAll.button.regeneratingLabel')}
                </>
              )}
              {!isRegenerating && (
                <>{intl.get('canonicalUrl.regenAll.button.label')}</>
              )}
            </button>
          </div>
          <div></div>
        </div>

        {websites.map((website) => {
          return (
            <RegenerateInput
              canonical_website={storyObj.canonical_website}
              website={website}
              key={website.id}
              parentRegenerating={isRegenerating}
              handleSingleRegeneration={handleSingleRegeneration}
              hasRegeneratePermissions={hasRegeneratePermissions}
              isDirty={isDirty}
              isPublished={isPublished}
              isScheduled={isScheduled}
            />
          )
        })}

        <div className="canonical-url--section-divider">
          <div>
            <hr />
          </div>
        </div>
      </section>

      <RegenerateAllModal
        handleRegenerate={handleRegenerateAll}
        websitesCount={websites.length}
      />

      {snackbar.success && isDirty && (
        <Snackbar
          status="Success"
          message={intl.formatMessage(
            {
              id: 'canonicalUrl.regenAll.snackbar.success',
            },
            {
              count: websitesCount.success,
            }
          )}
          timeout={3000}
          onTimeout={() => {
            setSnackbar({
              success: false,
            })
          }}
        />
      )}

      {snackbar.error && isDirty && (
        <Snackbar
          status={snackbar.success ? 'ErrorSuccess' : 'Error'}
          message={intl.formatMessage(
            {
              id: 'canonicalUrl.regenAll.snackbar.error',
            },
            {
              count: websitesCount.error,
            }
          )}
          timeout={3000}
          onTimeout={() => {
            setSnackbar({
              error: false,
            })
          }}
        />
      )}

      {snackbar.timeout && isDirty && (
        <Snackbar
          status={snackbar.success ? 'ErrorSuccess' : 'Error'}
          message={errorMessage}
          timeout={3000}
          onTimeout={() => {
            setSnackbar({
              timeout: false,
            })
          }}
        />
      )}

      {snackbar.tooManyRequest && isDirty && (
        <Snackbar
          status="Error"
          message={intl.formatMessage({
            id: 'canonicalUrl.regenAll.snackbar.tooManyRequest',
          })}
          timeout={3000}
          onTimeout={() => {
            setSnackbar({
              timeout: false,
            })
          }}
        />
      )}
    </>
  )
}
